package com.bikompanion.domain

/**
 * The side of a parking cell, in degrees — 11 km north to south, and 8 km across at 45° latitude.
 *
 * The grid covers the whole planet, so the size is a global compromise rather than a local one: a
 * cell is roughly a screenful at the zoom the layer draws racks at, and small enough that the
 * densest ground anywhere still fits in one. The busiest cell in the shipped dataset holds just
 * under 10,000 racks; at 0.2° that ground was more than the public Overpass instances would serve
 * in a single request at all.
 *
 * Cells are square in degrees, not in metres, so they narrow towards the poles. That is on
 * purpose: it is what keeps the index arithmetic exact and a cell id derivable from a coordinate
 * without a projection.
 */
const val PARKING_CELL_DEGREES = 0.1

/**
 * A fixed patch of the world that bike racks are fetched for, whole.
 *
 * Fixed rather than "whatever the camera is looking at" so that two viewports over the same
 * city ask for the same ground, and the second one is already in hand. A cell drawn from the
 * viewport instead would shift with every pan and never hit the cache.
 *
 * Not scoped to a network, deliberately: only some sources say where a network reaches —
 * CityBikes gives a single point — and racks are OpenStreetMap's, not any operator's, so tying
 * them to a network would leave them unfetchable on the source the app actually runs on.
 */
data class ParkingCell(
    val latitudeIndex: Int,
    val longitudeIndex: Int,
) {
    val bounds: BoundingBox
        get() = BoundingBox(
            south = edge(latitudeIndex),
            west = edge(longitudeIndex),
            north = edge(latitudeIndex + 1),
            east = edge(longitudeIndex + 1),
        )

    val centerLatitude: Double get() = round((latitudeIndex + 0.5) * PARKING_CELL_DEGREES)

    val centerLongitude: Double get() = round((longitudeIndex + 0.5) * PARKING_CELL_DEGREES)

    private fun edge(index: Int) = round(index * PARKING_CELL_DEGREES)

    companion object {
        fun containing(
            latitude: Double,
            longitude: Double,
        ) = ParkingCell(
            latitudeIndex = floorDiv(latitude),
            longitudeIndex = floorDiv(longitude),
        )

        /**
         * Every cell [box] covers, south-west first.
         *
         * Floored rather than truncated so cells line up across the equator and the prime
         * meridian: at -0.1, truncation gives cell 0 — the same cell as +0.1, half a world of
         * racks away from where the camera is.
         *
         * Half-open at the top and right, so a box that is exactly one cell is exactly one cell.
         * Counting the row above and the column beyond — touched only along a line of zero width
         * — meant asking for a cell's own [bounds] handed back four of them, and every rack in
         * the three neighbours was then drawn twice and counted twice in the area totals.
         */
        fun covering(box: BoundingBox): List<ParkingCell> {
            val southIndex = floorDiv(box.south)
            val westIndex = floorDiv(box.west)
            val northIndex = upperIndex(box.north, southIndex)
            val eastIndex = upperIndex(box.east, westIndex)
            return buildList {
                for (latitudeIndex in southIndex..northIndex) {
                    for (longitudeIndex in westIndex..eastIndex) {
                        add(ParkingCell(latitudeIndex, longitudeIndex))
                    }
                }
            }
        }

        /**
         * Trims the float noise off a multiple of [PARKING_CELL_DEGREES].
         *
         * Neither 0.2 nor 0.1 is exact in binary, so an index times the cell size lands a few
         * quadrillionths off the decimal it should be — `414 * 0.1` is 41.400000000000006. That
         * noise would go out in the query and into the cache key, where two runs that computed the
         * same cell slightly differently would miss each other and pay for the same ground twice.
         * Six decimals is a tenth of a metre, far finer than a cell edge needs to be.
         */
        internal fun round(coordinate: Double): Double = kotlin.math.round(coordinate * 1_000_000) / 1_000_000

        /**
         * The last cell a box reaches on one axis, given the first.
         *
         * An edge sitting exactly on a cell boundary belongs to the cell below it, not the one
         * above — see [covering]. Compared through [round] because a cell edge computed by
         * multiplication carries float noise that an exact `==` would miss.
         */
        private fun upperIndex(
            coordinate: Double,
            lowerIndex: Int,
        ): Int {
            val index = floorDiv(coordinate)
            val sitsOnTheEdge = round(index * PARKING_CELL_DEGREES) == round(coordinate)
            return if (index > lowerIndex && sitsOnTheEdge) index - 1 else index
        }

        /**
         * Which cell a coordinate falls in, flooring so cells line up across the equator and the
         * prime meridian: truncating puts -0.05 and +0.05 in cell 0 together, half a world of
         * racks away from where the camera is.
         *
         * Snapped first, because the division does not round-trip the grid's own edges: `41.4 /
         * 0.1` is 413.99999999999994, so a coordinate sitting exactly on a boundary floored into
         * the cell *below* it and `containing(cell.bounds.south)` did not give back `cell`. That
         * put the south and west edges of every cell in its neighbour.
         */
        private fun floorDiv(coordinate: Double): Int {
            val scaled = coordinate / PARKING_CELL_DEGREES
            val nearest = kotlin.math.round(scaled)
            if (kotlin.math.abs(scaled - nearest) < EdgeTolerance) return nearest.toInt()
            return kotlin.math.floor(scaled).toInt()
        }

        /** Far below a cell, far above the float noise — this is about a millimetre of ground. */
        private const val EdgeTolerance = 1e-9
    }
}
